import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resetPassword } from '../services/authService';
import { createCheckoutSession } from '../services/stripeService';
import { PRODUCTS } from '../stripe-config';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showReactivation, setShowReactivation] = useState(false);
  const [reactivationLoading, setReactivationLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const logLoginAttempt = async (success: boolean, errorType?: string, errorMessage?: string) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        console.warn('Supabase credentials not available for login logging');
        return;
      }

      await fetch(`${supabaseUrl}/functions/v1/log-login-attempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          success,
          errorType,
          errorMessage,
          ipAddress: null,
          userAgent: navigator.userAgent,
        }),
      });
    } catch (logError) {
      console.warn('Failed to log login attempt:', logError);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      setError('Bitte geben Sie E-Mail und Passwort ein');
      return;
    }

    setError('');
    setLoading(true);
    setShowReactivation(false);

    try {
      console.log('Attempting login with email:', email);
      const user = await login(email, password);
      console.log('Login successful, user data:', { id: user?.id, email: user?.email });

      await logLoginAttempt(true);

      if (user?.isAdmin === true) {
        navigate('/admin');
      } else {
        navigate('/kurszugang');
      }
    } catch (err: any) {
      console.error('Login error details:', err);
      let errorMessage = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
      let errorType = 'unknown_error';

      if (err.name === 'AccountDisabled') {
        errorType = 'account_disabled';
        errorMessage = 'Ihr Konto ist noch nicht aktiviert. Die Zahlung muss noch abgeschlossen werden. Bitte vervollständigen Sie Ihre Zahlung oder kontaktieren Sie den Support.';
      } else if (err.message === 'Invalid login credentials' || err.error?.message === 'Invalid login credentials') {
        errorType = 'invalid_credentials';
        errorMessage = 'E-Mail oder Passwort ist falsch. Bitte überprüfen Sie Ihre Eingaben oder nutzen Sie die "Passwort vergessen" Funktion.';
      } else if (err.message === 'Email not confirmed') {
        errorType = 'email_not_confirmed';
        errorMessage = 'Ihre E-Mail-Adresse wurde noch nicht bestätigt. Bitte öffnen Sie die Bestätigungs-E-Mail, die wir Ihnen nach der Registrierung zugesendet haben, und klicken Sie auf den darin enthaltenen Bestätigungslink. Prüfen Sie gegebenenfalls auch Ihren Spam-Ordner. Die Zustellung kann einige Minuten dauern.';
      } else if (err.message?.includes('network')) {
        errorType = 'network_error';
        errorMessage = 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
      } else if (err.message?.includes('session')) {
        errorType = 'session_error';
      }

      await logLoginAttempt(false, errorType, err.message || errorMessage);

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await resetPassword(email);
      setResetSent(true);
      console.log('Password reset email sent');
    } catch (err: any) {
      console.error('Password reset error:', err);
      const errorMessage = err.message || 'Fehler beim Zurücksetzen des Passworts. Bitte versuchen Sie es später erneut.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (resetSent) {
    return (
      <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Passwort zurücksetzen</h2>
        <div className="bg-green-50 text-green-800 p-4 rounded-md mb-6">
          <p>Eine E-Mail mit Anweisungen zum Zurücksetzen Ihres Passworts wurde an {email} gesendet.</p>
        </div>
        <button
          onClick={() => {
            setShowResetForm(false);
            setResetSent(false);
          }}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          Zurück zum Login
        </button>
      </div>
    );
  }

  if (showResetForm) {
    return (
      <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Passwort zurücksetzen</h2>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}
        
        <form onSubmit={handleResetPassword}>
          <div className="space-y-4">
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail
              </label>
              <input
                type="email"
                id="reset-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors ${
                loading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Wird gesendet...' : 'Passwort zurücksetzen'}
            </button>

            <button
              type="button"
              onClick={() => setShowResetForm(false)}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-md transition-colors mt-2"
            >
              Zurück zum Login
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Login</h2>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md" role="alert">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              E-Mail
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Passwort
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setShowResetForm(true)}
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              Passwort vergessen?
            </button>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors ${
              loading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Anmeldung läuft...' : 'Anmelden'}
          </button>
        </div>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Noch kein Konto? <a href="/registrieren" className="font-medium text-blue-600 hover:text-blue-500">
            Jetzt registrieren
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;